import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useContext,
  createContext,
  forwardRef,
} from "react";
import {
  Autocomplete,
  autocompleteClasses,
  Box,
  ListItem,
  Typography,
  Popover,
  Paper,
  popoverClasses,
  Tooltip,
} from "@analyticsMD/quil";
import {
  Button,
  InputLabel,
  OutlinedInput,
  FormControl,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";

import Milestone from "../../../types/Milestone";
import { styles, sxStyles } from "./styles";

interface FooterContextValue {
  count: number;
  onCancel: () => void;
  onDone: () => void;
}

const noop = (): void => undefined;

const FooterContext = createContext<FooterContextValue>({
  count: 0,
  onCancel: noop,
  onDone: noop,
});

// Module-level so its identity is stable across renders — changing
// Autocomplete's PaperComponent reference would remount the listbox and
// reset scroll on every selection. Footer state is read via context instead.
const CustomPaperComponent: React.FC = ({ children }) => {
  const { count, onCancel, onDone } = useContext(FooterContext);
  const addLabel =
    count === 1 ? "Add 1 milestone" : `Add ${count} milestones`;
  return (
    <Paper
      variant="outlined"
      sx={{
        [`.${autocompleteClasses.noOptions}`]: {
          display: "flex",
          justifyContent: "center",
        },
      }}
    >
      {children}
      <Box sx={sxStyles.buttonsWrapper}>
        <Button
          size="small"
          sx={sxStyles.cancelButton}
          // preventDefault on mousedown keeps focus on the autocomplete input
          // (so the popper doesn't close before the click fires); onClick still
          // fires for both mouse and keyboard (Enter/Space) activation.
          onMouseDown={(e) => e.preventDefault()}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          size="small"
          sx={sxStyles.addButton}
          onMouseDown={(e) => e.preventDefault()}
          onClick={onDone}
          disabled={count === 0}
        >
          {addLabel}
        </Button>
      </Box>
    </Paper>
  );
};

// Module-level statics: previously allocated fresh on every render.
const LISTBOX_PROPS = {
  style: sxStyles.listbox,
  className: styles.scrollbar,
  sx: sxStyles.autocompleteListbox,
};

const POPOVER_SX = {
  [`.${popoverClasses.paper}`]: {
    borderRadius: "0.25rem",
    border: 0,
  },
};

const POPOVER_ANCHOR_ORIGIN = { vertical: 1, horizontal: "left" } as const;

const INPUT_LABEL_SX = {
  ...sxStyles.inputLabel,
  ...sxStyles.milestoneInputLabelFocused,
};

interface MilestonesModalProps {
  open: boolean;
  // Nullable so `ref.current` from the parent can be passed directly before it
  // attaches; internal usage already guards with `?.` and a width fallback.
  anchorElement: HTMLElement | null;
  options: Milestone[];
  disabledOptionIds?: Set<number>;
  onCancel?: () => void;
  onSave?: (milestones?: Milestone[]) => void;
  onClose?: () => void;
}

export interface MilestonesModalHandle {
  focusInput: () => void;
}

const MilestonesModal = forwardRef<MilestonesModalHandle, MilestonesModalProps>(
  (
    {
      open,
      anchorElement,
      options,
      disabledOptionIds,
      onCancel,
      onSave,
      onClose,
    },
    ref
  ) => {
    const [tagsOverflow, setTagsOverflow] = useState<boolean>(false);
    // newMilestones keeps up to date the state of autocomplete component
    const [newMilestones, setNewMilestones] = useState([]);
    const boxRef = useRef();
    const inputRef = useRef<HTMLInputElement>(null);
    // newMilestonesRef is used to keep track of newMilestones without causing re-renders
    const newMilestonesRef = useRef([]);

    useImperativeHandle(ref, () => ({
      focusInput: () => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      },
    }));

    const handleCancel = useCallback(() => {
      setNewMilestones([]);
      newMilestonesRef.current = [];
      onCancel?.();
    }, [onCancel]);

    const handleDone = useCallback(() => {
      if (newMilestonesRef.current.length > 0) {
        onSave?.(newMilestonesRef.current);
        setNewMilestones([]);
        newMilestonesRef.current = [];
      }
      onClose?.();
    }, [onSave, onClose]);

    const footerValue = useMemo<FooterContextValue>(
      () => ({
        count: newMilestones.length,
        onCancel: handleCancel,
        onDone: handleDone,
      }),
      [newMilestones.length, handleCancel, handleDone]
    );

    // Only anchorElement changes at runtime; keep the memo dep list tight so
    // Autocomplete doesn't see a fresh componentsProps identity per render.
    const autocompleteComponentsProps = useMemo(
      () => ({
        popper: {
          placement: "bottom" as const,
          keepMounted: true,
          anchorEl: anchorElement,
          sx: sxStyles.popperSpacing,
        },
      }),
      [anchorElement]
    );

    const checkOverflow = (elem: HTMLElement) => {
      //Check overflow by checking if input space is reached its minimum width
      const parentElement = elem.parentElement;
      const inputElement = parentElement.children[1];
      const nodeStyle = window.getComputedStyle(inputElement);
      const paddingLeft = Number(
        nodeStyle.getPropertyValue("padding-left").replace("px", "")
      );
      const paddingRight = Number(
        nodeStyle.getPropertyValue("padding-right").replace("px", "")
      );
      const minWidth = Number(
        nodeStyle.getPropertyValue("min-width").replace("px", "")
      );

      const totalMinWidth = paddingLeft + paddingRight + minWidth;
      const currentWidth = inputElement.clientWidth;
      return currentWidth <= totalMinWidth;
    };

    useEffect(() => {
      if (boxRef.current) {
        setTagsOverflow(checkOverflow(boxRef.current));
      }
    }, [newMilestones]);

    useEffect(() => {
      if (open && inputRef.current) {
        // Focus the input when the modal opens to trigger the autocomplete dropdown
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
      }
    }, [open]);
    return (
      <Popover
        elevation={0}
        open={open}
        anchorEl={anchorElement}
        anchorOrigin={POPOVER_ANCHOR_ORIGIN}
        PaperProps={{
          variant: "outlined",
          style: {
            display: "flex",
            flexDirection: "column",
            overflowY: "hidden",
            width: `${anchorElement?.getBoundingClientRect().width ?? 0}px`,
          },
        }}
        sx={POPOVER_SX}
      >
        <FooterContext.Provider value={footerValue}>
          <Autocomplete
            openOnFocus
            autoHighlight
            multiple
            disableCloseOnSelect
            options={options}
            value={newMilestones}
            componentsProps={autocompleteComponentsProps}
            PaperComponent={CustomPaperComponent}
            ListboxProps={LISTBOX_PROPS}
            sx={sxStyles.autocompleteRoot}
            filterOptions={(opts, { inputValue }) => {
              const q = inputValue.trim().toLowerCase();
              if (!q) return opts;
              return opts.filter(
                (o) =>
                  o.display_name.toLowerCase().includes(q) ||
                  (o.tags ?? []).some((t) => t.toLowerCase().includes(q))
              );
            }}
            onChange={(event: any, newValue: Milestone[] | null) => {
              // Autocomplete in multiple mode returns an array, but the type
              // includes null — coalesce so downstream .length reads stay safe.
              const next = newValue ?? [];
              newMilestonesRef.current = next;
              setNewMilestones(next);
            }}
            onClose={() => {
              newMilestonesRef.current = [];
              setNewMilestones([]);
              onClose?.();
            }}
            getOptionLabel={(option: Milestone) => option.display_name}
            getOptionDisabled={(option: Milestone) =>
              !!disabledOptionIds?.has(option.id)
            }
            renderOption={(props, option: Milestone, { selected }) => {
              const isExisting = !!disabledOptionIds?.has(option.id);
              return (
                <ListItem
                  {...props}
                  key={`milestone-option-${option.id}`}
                  sx={
                    isExisting ? sxStyles.listItemDisabled : sxStyles.listItem
                  }
                >
                  <Box sx={sxStyles.optionLabel}>{option.display_name}</Box>
                  {(selected || isExisting) && (
                    <CheckIcon
                      sx={
                        isExisting
                          ? sxStyles.optionCheckIconDisabled
                          : sxStyles.optionCheckIcon
                      }
                      fontSize="small"
                    />
                  )}
                </ListItem>
              );
            }}
            renderInput={(params) => {
              const isEmpty = newMilestones.length === 0;
              return (
                <FormControl
                  variant="outlined"
                  sx={{
                    width: "100%",
                    [`& .${autocompleteClasses.inputRoot}`]: {
                      flexWrap: "nowrap",
                      ...sxStyles.milestoneOutlinedInput,
                    },
                    [`& .${autocompleteClasses.input}`]: {
                      flex: isEmpty ? "1 1 auto" : "0 0 0",
                      minWidth: isEmpty ? "50px" : "0 !important",
                      width: isEmpty ? "auto" : "0",
                      padding: isEmpty ? undefined : "0",
                      opacity: isEmpty ? 1 : 0,
                    },
                  }}
                >
                  <InputLabel
                    sx={INPUT_LABEL_SX}
                    htmlFor="milestone-input"
                    shrink
                  >
                    Milestones
                  </InputLabel>
                  <OutlinedInput
                    {...params.InputProps}
                    id="milestone-input"
                    style={sxStyles.outlinedInput}
                    label="Milestones"
                    notched
                    inputProps={{
                      ...params.inputProps,
                      ref: (node: HTMLInputElement) => {
                        // Merge refs: call both the Autocomplete's ref and our ref
                        const autocompleteRef = (params.inputProps as any).ref;
                        if (typeof autocompleteRef === "function") {
                          autocompleteRef(node);
                        } else if (autocompleteRef) {
                          (
                            autocompleteRef as React.MutableRefObject<HTMLInputElement | null>
                          ).current = node;
                        }
                        inputRef.current = node;
                      },
                      placeholder: isEmpty ? "Search for milestones" : "",
                      style: sxStyles.outlinedInputProp as React.CSSProperties,
                    }}
                  />
                </FormControl>
              );
            }}
            renderTags={(tags) => {
              const allTags = tags.map((t) => t.display_name).join(", ");
              return (
                <Tooltip
                  title={<Typography variant="caption">{allTags}</Typography>}
                  placement="top"
                  disableHoverListener={!tagsOverflow}
                  arrow
                >
                  <Box ref={boxRef} component="span" sx={sxStyles.tagText}>
                    {allTags}
                  </Box>
                </Tooltip>
              );
            }}
          />
        </FooterContext.Provider>
      </Popover>
    );
  }
);

MilestonesModal.displayName = "MilestonesModal";

// Note: previously wrapped in React.memo with a custom comparator that only
// looked at `open`, `options`, and `disabledOptionIds`. That skipped re-renders
// when callback props (e.g. onSave) took on a new closure — leading to stale
// handlers firing after optimistic updates. CustomPaperComponent is now a
// module-level component, so re-renders don't remount the listbox / reset
// scroll, and the band-aid is no longer worth its correctness cost.
export default MilestonesModal;
